import type { Dayjs } from '@seolens/date-manager';

import { ArrayElement } from '@/common/interfaces';

import { SearchConsoleAnalytics, SearchConsoleAnalyticsRow } from '@/modules/DomainsApi';

import { Domain } from '@/modules/Domains';
import { FormFields, FormValidationSchema } from '../../common/interfaces';

export enum SiteSearchConsoleFormInputs {
  SearchConsoleAccountId = 'searchConsoleAccountId',
}

export type SiteSearchConsoleFormFields = FormFields<typeof SiteSearchConsoleFormInputs>;

export type SiteSearchConsoleFormValidationSchema = FormValidationSchema<SiteSearchConsoleFormFields>;

export enum SearchConsoleAddressChangingStatus {
  Pending = 'pending',
  Redirecting = 'redirecting',
  Success = 'success',
  Fail = 'fail',
  None = 'none'
}

export type SearchConsoleAddressChangingLogs = {
  domain: {
    id: string;
    name: string;
    siteUrl: string;
  };
  errorMessage: string | null;
  makesRedirects: boolean;
};

export type SearchConsoleAddressChangingInfo = {
  lastChangedMainDomain: (Pick<Domain, 'id' | 'name' | 'isGoogleVerificated'> & { siteUrl: string }) | null;
  currentChangedMainDomain: (Pick<Domain, 'id' | 'name' | 'isGoogleVerificated'> & { siteUrl: string }) | null;
  changingStatus: SearchConsoleAddressChangingStatus;
  changedAt: Date | null;
  changingError: string | null;
  changingLogs: SearchConsoleAddressChangingLogs[];
};

export enum SiteSearchConsoleGatewayKeys {
  UpdateRedirectingStatus = 'updateRedirectingStatus',
  UpdateProcessingStatus = 'updateProcessingStatus',
}

export enum SearchConsoleAccountProcessStatus {
  Pending = 'pending',
  Set = 'set',
  Setted = 'setted',
  Unsetted = 'unsetted',
  Unset = 'unset',
  Failed = 'failed',
  None = 'none',
}

export enum SearchConsoleAnalyticsFormInputs {
  DataState = 'dataState',
  StartDate = 'startDate',
  EndDate = 'endDate',
}

export type SearchConsoleAnalyticsFormFields = FormFields<typeof SearchConsoleAnalyticsFormInputs>;

export type SearchConsoleAnalyticsFormValidationSchema = FormValidationSchema<SearchConsoleAnalyticsFormFields>;

export type SearchConsoleAnalyticsRequestData = {
  dataState: 'all' | 'final';
  // calendar dates 'YYYY-MM-DD', not instants: the backend reads them as Pacific Time dates
  startDate: string;
  endDate: string;
};

export type SearchConsoleAnalyticsPresetPeriod = '7d' | '28d' | '3m' | '6m' | '12m' | '16m';

export type SearchConsoleAnalyticsPeriod = SearchConsoleAnalyticsPresetPeriod | 'custom';

export const DEFAULT_SEARCH_CONSOLE_ANALYTICS_PERIOD: SearchConsoleAnalyticsPresetPeriod = '3m';

// Search Console dates are in Pacific Time
export const SEARCH_CONSOLE_TIME_ZONE = 'America/Los_Angeles';

// inclusive ranges ending today, same as in Search Console
export const SEARCH_CONSOLE_ANALYTICS_PERIOD_START_DATE: Record<SearchConsoleAnalyticsPresetPeriod, (endDate: Dayjs) => Dayjs> = {
  '7d': (endDate) => endDate.subtract(6, 'day'),
  '28d': (endDate) => endDate.subtract(27, 'day'),
  '3m': (endDate) => endDate.subtract(3, 'month').add(1, 'day'),
  '6m': (endDate) => endDate.subtract(6, 'month').add(1, 'day'),
  '12m': (endDate) => endDate.subtract(12, 'month').add(1, 'day'),
  '16m': (endDate) => endDate.subtract(16, 'month').add(1, 'day')
};

export const SEARCH_CONSOLE_ANALYTICS_QUICK_PERIODS: { key: SearchConsoleAnalyticsPeriod; label: string }[] = [
  { key: '7d', label: '7 днів' },
  { key: '28d', label: '28 днів' },
  { key: '3m', label: '3 місяці' }
];

export const SEARCH_CONSOLE_ANALYTICS_MORE_PERIODS: { key: SearchConsoleAnalyticsPeriod; label: string }[] = [
  { key: '6m', label: '6 місяців' },
  { key: '12m', label: '12 місяців' },
  { key: '16m', label: '16 місяців' },
  { key: 'custom', label: 'Спеціальний' }
];


export type SearchConsoleAnalyticsRowKeys = Exclude<keyof SearchConsoleAnalyticsRow<string>, 'key'>

export type SearchConsoleAnalyticsDimensions = Omit<SearchConsoleAnalytics['data'], 'default'>;

export type SearchConsoleAnalyticsDimensionTypes = {
  [x in Required<keyof SearchConsoleAnalyticsDimensions>]: NonNullable<ArrayElement<SearchConsoleAnalyticsDimensions[x]>>
}

export type SearchConsoleAnalyticsDimensionsKeys = keyof SearchConsoleAnalyticsDimensions;

export type SearchConsoleAnalyticsRowKeysColor = Record<SearchConsoleAnalyticsRowKeys, string>;
